<template>
  <ProvideStateTriggeredBySlotProps v-slot="{ isOn, toggle, turnOff, turnOn }">
    <div
      v-click-outside="turnOff"
      v-hover-intent="{
        in: fZip(
          () => {
            !customTrigger && turnOn()
          },
          update,
          reLock,
          enter,
        ),
        out: awaitClose(turnOff),
      }"
      v-bind="$attrs"
      class="inline"
    >
      <BasePopper
        ref="popper"
        default-slot-is-inline
        :display="isOn"
        :animate="true"
        :placement="placement"
        :strategy="strategy"
        :global="global"
        :arrow-class="{
          'top-bar-arrow': placement === 'bottom',
          ...styleClass,
        }"
      >
        <div class="inline">
          <slot
            :toggle="fZip(toggle, update)"
            :is-on="isOn"
            :turn-off="fZip(turnOff, update)"
            :turn-on="fZip(turnOn, update)"
          />
        </div>

        <template #popper>
          <div :class="styleClass" class="popover">
            <slot name="title-wrap">
              <h3 class="popover-title"><slot name="title" /></h3>
            </slot>
            <slot name="content-wrap">
              <div v-if="$slots.content" class="popover-content">
                <slot name="content" />
              </div>
            </slot>
          </div>
        </template>
      </BasePopper>
    </div>
  </ProvideStateTriggeredBySlotProps>
</template>

<script>
import { debounce } from 'lodash/function'
import BasePopper from '@/components/BasePopper.vue'
import FunctionZipMixin from '@/mixins/MethodFZipMixin'
import ProvideStateTriggeredBySlotProps from '@/components/ProvideStateTriggeredBySlotProps.vue'

export default {
  name: 'PopperCard',
  components: { BasePopper, ProvideStateTriggeredBySlotProps },
  mixins: [FunctionZipMixin],
  props: {
    customTrigger: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'left',
    },
    strategy: {
      type: String,
      default: 'fixed',
    },
    styleClass: {
      // Either none, for default, or loading for loading style or error for error style
      type: Object,
      default: () => {},
    },
    global: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      relocked: false,
      awaitingClose: false,
    }
  },
  methods: {
    enter($event) {
      this.$emit('hover', $event)
    },
    reLock() {
      this.reLocked = this.awaitingClose
    },
    awaitClose(func) {
      return () => {
        if (this.customTrigger) return
        this.awaitingClose = true
        debounce(() => {
          // We check to make sure that we haven't accidentally started to rehover over our card ie we moved over the
          // invisible gab between the card and the content which would result in the popper hiding even if we want
          // to interact with it. This stops it from closing accidentally
          if (this.reLocked) {
            this.reLocked = false
          } else {
            func()
          }
          this.awaitingClose = false
        }, 150)()
      }
    },
    update() {
      this.$refs.popper.update()
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/variable-overrides';
@import 'resources/sass/frontend/_color-variables';
.popover {
  min-width: 300px;
  position: initial;
  display: block;

  .popover-title {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-right: 45px;
  }

  &.loading {
    .popover-title {
      background-color: #0e0d12;
    }
  }

  &.error {
    .popover-title {
      background-color: $alert-red;
    }
  }

  &.error,
  &.loading {
    .popover-title {
      border-radius: 5px !important;
    }
    min-width: 0;
  }
}

:deep(.top-bar-arrow::before) {
  background: $grayPrimary;
}
:deep(.loading::before) {
  background: $grayPrimary;
}
:deep(.error::before) {
  background: $alert-red !important;
}
</style>
